// ORC-41 / M1 Slice 11-12 closure — PKCE (RFC 7636) utility for the
// SPA's OIDC authorization-code flow.
//
// Two-step contract (per ORC_41_PLAN §6):
//   1. At /login → /auth/discover success the SPA calls
//      createPkcePair(), stores the verifier via storePkceVerifier(state),
//      and appends `code_challenge=…&code_challenge_method=S256` to the
//      authorize URL before `window.location.assign`.
//   2. On the /login?code=…&state=… callback the SPA calls
//      consumePkceVerifier(state) and POSTs the verifier to
//      /v1/auth/exchange (which already plumbs code_verifier + redirect_uri
//      to the IdP per HttpIdpClient.ExchangeCodeAsync).
//
// Storage: sessionStorage keyed by "rc.pkce.<state>"; values are 43-char
// base64url verifiers. sessionStorage is correct because the verifier is
// single-use and must not survive a tab close (OAuth 2.1 §4.1 / Keycloak
// guidance). consumePkceVerifier reads-and-deletes atomically to enforce
// single-use semantics.
//
// Implementation notes:
//   • Native Web Crypto API only — no new dep. crypto.getRandomValues +
//     crypto.subtle.digest are available everywhere the SPA runs.
//   • 32 random bytes → 43-char base64url verifier (well above RFC 7636
//     §4.1 minimum of 43 chars; within the 43..128 unreserved range).
//   • base64url = base64 with `+→-`, `/→_`, strip trailing `=`.
//   • Error code "pkce.crypto_unavailable" surfaces as
//     t("login.error.unsupported_browser") in LoginScreen.

const STORAGE_PREFIX = "rc.pkce.";

/**
 * Encode bytes as base64url (RFC 4648 §5 — base64 without padding,
 * `+` → `-`, `/` → `_`).
 */
function base64UrlEncode(bytes: Uint8Array): string {
  // chunk-by-chunk to avoid the 64k arg-cap on String.fromCharCode for
  // very large buffers (defensive — we only ever feed 32-byte or
  // 32-byte SHA-256 outputs, but the helper is generic).
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

/**
 * Generate a fresh PKCE verifier + S256 challenge pair.
 *
 * Throws "pkce.crypto_unavailable" if the runtime lacks
 * `crypto.subtle.digest` (very old browsers / non-secure contexts).
 * LoginScreen maps the message to t("login.error.unsupported_browser").
 */
export async function createPkcePair(): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> {
  if (typeof crypto === "undefined" || !crypto.subtle) {
    throw new Error("pkce.crypto_unavailable");
  }
  const rand = new Uint8Array(32);
  crypto.getRandomValues(rand);
  const codeVerifier = base64UrlEncode(rand); // 43 chars
  const verifierBytes = new TextEncoder().encode(codeVerifier);
  const digest = await crypto.subtle.digest("SHA-256", verifierBytes);
  const codeChallenge = base64UrlEncode(new Uint8Array(digest));
  return { codeVerifier, codeChallenge };
}

/**
 * Persist `verifier` under the namespaced storage key for `state`.
 *
 * No throw — if `sessionStorage` is unavailable (privacy-mode iOS,
 * embedded WebView with storage disabled) the callback handler will
 * later surface "session expired" via consumePkceVerifier returning
 * null.
 */
export function storePkceVerifier(state: string, verifier: string): void {
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + state, verifier);
  } catch {
    /* sessionStorage unavailable: callback recovery surfaces session_expired */
  }
}

/**
 * Read + delete the verifier for `state` atomically. Returns `null` if
 * the entry is missing or sessionStorage is unavailable.
 *
 * Single-use semantics: a subsequent call for the same `state` always
 * returns `null`, even on success, so a back/forward navigation can not
 * accidentally re-post the same code.
 */
export function consumePkceVerifier(state: string): string | null {
  try {
    const key = STORAGE_PREFIX + state;
    const v = window.sessionStorage.getItem(key);
    if (v !== null) {
      window.sessionStorage.removeItem(key);
    }
    return v;
  } catch {
    return null;
  }
}
